/**
 * adminOrderingScheduling — the admin tRPC scope-ordering (D-128,
 * ordering-as-draft) and server-side scheduled-publishing (024-cms US5)
 * mutations + queries. Part of the `useAdminContent` decomposition.
 *
 * Scope-ordering (024-cms / D-128 — ordering-as-draft)
 * ===================================================
 *
 * D-128 replaced the publish-gated immediate-commit `reorderScope` /
 * `reorderCategories` with a DRAFT-tracked model:
 *
 *   - `stageScopeOrder` writes the DRAFT order on every member whose target
 *     rank differs from its live ordinal (D-198; on a dense group, the moved
 *     rows) — the live order is unchanged until publish. Fired by the plan's
 *     order section (`OrderPlanSection` / Luonnokset) inline drag on each drop.
 *   - `publishScopeOrder` applies the scope's STAGED order atomically, sourced
 *     from the staged deltas (NO `orderedItemIds`). Invoked BOTH by the
 *     standalone "Julkaise järjestys" control (`OrderPlanSection`) AND folded
 *     into a content publish (see `usePublishWithReconcile`); both coexist (D-132).
 *
 * A stale staged arrangement maps to a tRPC `CONFLICT` carrying the
 * `STALE_ARRANGEMENT` marker — same mapping the removed `reorderScope` used.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import type { WebTrpcClient } from '../../../trpc/client.js';
import { ADMIN_CONTENT_LIST_QUERY_KEY } from './adminContentQueries.js';
import { useAdminTrpc } from './adminTrpcContext.js';
import { useCommittedToast } from './lifecycleToast.js';

// ---------------------------------------------------------------------
// Scope-ordering mutations (024-cms / D-128 — ordering-as-draft)
// ---------------------------------------------------------------------

/**
 * Input shape inferred from the `admin.content.stageScopeOrder` procedure —
 * `{ scope: { kind, key }, locale, orderedItemIds }`. Lifted from the client
 * surface so the catalog's staging payload stays locked to the wire contract
 * (the `kind` enum, the UUID-array `orderedItemIds`) without restating it.
 */
export type StageScopeOrderInput = Parameters<
  WebTrpcClient['admin']['content']['stageScopeOrder']['mutate']
>[0];

export type StageScopeOrderResult = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['stageScopeOrder']['mutate']>
>;

/** Input shape inferred from `admin.content.publishScopeOrder` —
 * `{ scope: { kind, key }, locale }` (no `orderedItemIds`; the order is sourced
 * from the staged deltas server-side). */
export type PublishScopeOrderInput = Parameters<
  WebTrpcClient['admin']['content']['publishScopeOrder']['mutate']
>[0];

export type PublishScopeOrderResult = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['publishScopeOrder']['mutate']>
>;

/**
 * Mutate `admin.content.stageScopeOrder` — stage a single disjoint scope's new
 * arrangement as a DRAFT (D-128). The live (member-visible) order is unchanged;
 * the staged order is committed later via `publishScopeOrder` (the standalone
 * "Julkaise järjestys" control or the publish-time fold).
 *
 * On success the `admin.content.list` cache is invalidated so the catalog
 * re-reads (the staged order surfaces in the draft tab / staged overlay).
 */
export function useStageScopeOrder(): UseMutationResult<
  StageScopeOrderResult,
  Error,
  StageScopeOrderInput
> {
  const client = useAdminTrpc();
  const queryClient = useQueryClient();
  return useMutation<StageScopeOrderResult, Error, StageScopeOrderInput>({
    mutationFn: (input) => client.admin.content.stageScopeOrder.mutate(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_CONTENT_LIST_QUERY_KEY });
    },
  });
}

/**
 * Mutate `admin.content.publishScopeOrder` — apply a scope's STAGED order
 * atomically (D-128), sourced from the staged `draft_display_order` deltas (no
 * `orderedItemIds` passed). Invoked BOTH by the standalone "Julkaise järjestys"
 * control (`OrderPlanSection`) AND composed into a content publish
 * (`usePublishWithReconcile`); both coexist (D-132) and call this same idempotent
 * procedure. On success the
 * `admin.content.list` cache is invalidated so the catalog re-reads the
 * now-live order. A no-op (nothing staged) returns the existing live order.
 */
export function usePublishScopeOrder(): UseMutationResult<
  PublishScopeOrderResult,
  Error,
  PublishScopeOrderInput
> {
  const client = useAdminTrpc();
  const queryClient = useQueryClient();
  const announce = useCommittedToast();
  return useMutation<PublishScopeOrderResult, Error, PublishScopeOrderInput>({
    mutationFn: (input) => client.admin.content.publishScopeOrder.mutate(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_CONTENT_LIST_QUERY_KEY });
      announce('cms.order.success.published');
    },
  });
}

/** Input shape inferred from `admin.content.discardScopeOrder` — the same
 * scope + locale selector as publish; nothing is submitted. */
export type DiscardScopeOrderInput = Parameters<
  WebTrpcClient['admin']['content']['discardScopeOrder']['mutate']
>[0];
export type DiscardScopeOrderResult = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['discardScopeOrder']['mutate']>
>;

/**
 * Mutate `admin.content.discardScopeOrder` — abandon a scope's STAGED order,
 * the author's way back from an arrangement they no longer want ("Hylkää
 * järjestysmuutos" in the plan's order section). The server removes the staged
 * arrangement and nothing else — live order, content drafts and member
 * visibility are untouched — and audits the discard as its own reorder step.
 * On success the `admin.content.list` cache is invalidated so the catalog
 * re-reads: the scope's rows drop their staged positions and the Julkaistut
 * pills return.
 */
export function useDiscardScopeOrder(): UseMutationResult<
  DiscardScopeOrderResult,
  Error,
  DiscardScopeOrderInput
> {
  const client = useAdminTrpc();
  const queryClient = useQueryClient();
  const announce = useCommittedToast();
  return useMutation<DiscardScopeOrderResult, Error, DiscardScopeOrderInput>({
    mutationFn: (input) => client.admin.content.discardScopeOrder.mutate(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_CONTENT_LIST_QUERY_KEY });
      announce('cms.order.success.discarded');
    },
  });
}

// ---------------------------------------------------------------------
// Scheduling (024-cms US5 — server-side scheduled publishing)
// ---------------------------------------------------------------------

/** One `admin.content.listScheduled` row, inferred from the procedure output. */
export type ScheduledListItem = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['listScheduled']['query']>
>[number];

export type SchedulePublishInput = Parameters<
  WebTrpcClient['admin']['content']['schedulePublish']['mutate']
>[0];

export type CancelScheduleInput = Parameters<
  WebTrpcClient['admin']['content']['cancelSchedule']['mutate']
>[0];

/** Stable query key for the `listScheduled` family — the invalidation target the
 * schedule mutations sweep so the panel + the Aloitus card re-read. */
export const ADMIN_SCHEDULED_LIST_QUERY_KEY = ['admin', 'content', 'listScheduled'] as const;

/**
 * Query `admin.content.listScheduled` — drives the per-item schedule panel's
 * pending state and the Aloitus card's `(N)` count. Filters to `status='pending'`
 * SERVER-side: only the upcoming (pending) rows are ever fetched, so the query
 * stays bounded as executed/failed/cancelled/skipped rows accumulate in
 * `scheduled_publications` (the consumers' client-side `=== 'pending'` checks then
 * stay correct + defensive). Requires the `AdminTrpcProvider` (like every admin
 * hook); the production admin surface and the test harnesses mount it.
 */
export function useScheduledList(options?: {
  /**
   * When `false`, the query never fires (react-query `enabled` gate). Lets a
   * conditional consumer — the unit take-down's pending-window read, mounted
   * per catalog row — subscribe only where the answer can matter, instead of
   * issuing the list read from every row. Default `true` (the schedule panel
   * and the Aloitus card always want it).
   */
  readonly enabled?: boolean;
}): UseQueryResult<readonly ScheduledListItem[], Error> {
  const client = useAdminTrpc();
  return useQuery<readonly ScheduledListItem[], Error>({
    queryKey: [...ADMIN_SCHEDULED_LIST_QUERY_KEY],
    queryFn: () => client.admin.content.listScheduled.query({ status: 'pending' }),
    enabled: options?.enabled ?? true,
  });
}

/**
 * The PENDING windows on one publication unit, read off its ROOT id — the
 * one derivation the catalog row marker, the card step and the action map all
 * consume. Publish first, then unpublish: the ordering guard
 * (`assertScheduleWindowOrdered`) guarantees a unit's publish precedes its
 * unpublish, so reading order is time order. Subscribes to the same
 * `listScheduled` query as the Aloitus card and the schedule panel (one fetch,
 * deduplicated), so it clears whenever that list re-reads: a schedule change
 * from this client, a tab switch, a return from the editor.
 */
export function usePendingWindows(unitRootId: string): {
  readonly windows: readonly ScheduledListItem[];
  readonly actions: readonly ScheduledListItem['action'][];
} {
  const scheduledList = useScheduledList();
  const windows = (scheduledList.data ?? [])
    .filter((entry) => entry.contentItemId === unitRootId && entry.status === 'pending')
    .sort((a, b) => WINDOW_ORDER[a.action] - WINDOW_ORDER[b.action]);
  return { windows, actions: windows.map((entry) => entry.action) };
}

const WINDOW_ORDER: Record<ScheduledListItem['action'], number> = { publish: 0, unpublish: 1 };

/**
 * Shared success-side sweep for any schedule change. Two keys, for two
 * different reasons — and they are worth keeping straight:
 *
 *   - `listScheduled` re-reads because the mutation genuinely changed it: the
 *     panel's pending state and the Aloitus count both consult it.
 *   - `admin.content.list` re-reads even though the mutation changed NOTHING in
 *     it. The list entry carries no schedule field at all, so creating, moving
 *     or cancelling a schedule cannot alter a catalog row's data. It is swept
 *     because a schedule call is the one moment the client is talking to the
 *     server about this item, which makes it the cheapest place to reconcile a
 *     catalog that a WORKER may already have changed while nobody was looking:
 *     a schedule fires server-side with no client request, so no
 *     mutation-triggered invalidation can fire for the fire itself.
 *
 * That second sweep is therefore a RECONCILIATION, not a consequence. Do not
 * "simplify" it away on the grounds that the mutation does not touch the
 * catalog — that is true, and is not the reason it is here.
 */
function invalidateAfterScheduleChange(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: ADMIN_SCHEDULED_LIST_QUERY_KEY });
  void queryClient.invalidateQueries({ queryKey: ADMIN_CONTENT_LIST_QUERY_KEY });
}

/** Mutate `admin.content.schedulePublish` — create a pending schedule. */
export function useSchedulePublish(): UseMutationResult<void, Error, SchedulePublishInput> {
  const client = useAdminTrpc();
  const queryClient = useQueryClient();
  const announce = useCommittedToast();
  return useMutation<void, Error, SchedulePublishInput>({
    mutationFn: (input) => client.admin.content.schedulePublish.mutate(input),
    onSuccess: () => {
      invalidateAfterScheduleChange(queryClient);
      // "Ajastus tallennettu", not "Julkaisu ajastettu": one submit can arm a
      // publish window, an unpublish window or both, so naming one action
      // would be false for the others.
      announce('cms.schedule.success.saved');
    },
  });
}

/** Mutate `admin.content.reschedulePublish` — move the pending schedule. */
export function useReschedulePublish(): UseMutationResult<void, Error, SchedulePublishInput> {
  const client = useAdminTrpc();
  const queryClient = useQueryClient();
  const announce = useCommittedToast();
  return useMutation<void, Error, SchedulePublishInput>({
    mutationFn: (input) => client.admin.content.reschedulePublish.mutate(input),
    onSuccess: () => {
      invalidateAfterScheduleChange(queryClient);
      announce('cms.schedule.success.changed');
    },
  });
}

/** Mutate `admin.content.cancelSchedule` — cancel the pending schedule. */
export function useCancelSchedule(): UseMutationResult<void, Error, CancelScheduleInput> {
  const client = useAdminTrpc();
  const queryClient = useQueryClient();
  const announce = useCommittedToast();
  return useMutation<void, Error, CancelScheduleInput>({
    mutationFn: (input) => client.admin.content.cancelSchedule.mutate(input),
    onSuccess: () => {
      invalidateAfterScheduleChange(queryClient);
      announce('cms.schedule.success.cancelled');
    },
  });
}
